import { Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import RecommendationCreate from '/@/components/recommendation-create';
import FieldSelect from '/@/components/recommendation-create/bayer/fields-step';
import Spinner from '/@/components/spinner';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import { useCatalogProductById } from '/@/hooks/catalog';
import { useFieldById } from '/@/hooks/fields';
import {
    getClimatePatternChoices,
    useDefaultCropConfig,
    useSimplifiedDefaultCropConfig,
} from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { updateActiveRecommendation, updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import { getAppInstance, getSessionBrands, getSessionCountry } from '/@/store/session/selectors';
import type {
    EVENTS as SEED_RECOMMENDATION_EVENTS,
    SeedRecommendationManagementTab,
    SeedRecommendationStep,
    SeedRecommendationType,
} from '/@/store/tracking/events/seed-recommendation';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import type { Recommendation } from '/@/types/recommendation';
import type { Step } from '/@/types/recommendation-create';
import { formatDate } from '/@/utils';
import { reverse } from '/@/utils/router';
import {
    flowEventProps,
    SeedRecommendationFlowContext,
    trackStepChange,
} from '/@/views/recommendations/v2/flow-tracking';
import ConditionsStep from '/@/views/recommendations/v2/steps/conditions';
import EnvironmentStep from '/@/views/recommendations/v2/steps/environment';
import HybridsStep from '/@/views/recommendations/v2/steps/hybrids';
import FieldSummary from '/@/views/recommendations/v2/steps/hybrids/field-summary';
import { useFieldRisks } from '/@/views/recommendations/v2/steps/hybrids/use-field-risks';
import { useHybrids } from '/@/views/recommendations/v2/steps/hybrids/use-hybrids';
import ManagementStep from '/@/views/recommendations/v2/steps/management';
import { useManagementData } from '/@/views/recommendations/v2/steps/management/use-management-data';
import ProductStep from '/@/views/recommendations/v2/steps/product';
import { useRecommendationResultData } from '../recommendation-aio/hooks';
import { seedRecommendationUrl } from '../seed-recommendation-route';
import { buildRecommendationBody, type RecommendationVariant, useSaveRecommendation } from '../use-save-recommendation';

type SeedRecommendationEntrySource = Parameters<
    (typeof SEED_RECOMMENDATION_EVENTS)['Seed recommendation began']
>[0]['entry_source'];

/**
 * Which questions the wizard asks, and in what order.
 *
 * `hybrid` ranks hybrids for a chosen field and takes several; `management` takes the single
 * hybrid the grower already bought; `field-for-hybrid` turns it around — the hybrid is fixed and
 * the wizard looks for the field that suits it, so the conditions have to be answered before the
 * field list can be ranked. All three end on the same management step and save through the same
 * payload builder, so they share this view rather than forking it and drifting.
 *
 * The flow decides the questions, not the record: both flows that start from a known hybrid are
 * stored as management recommendations.
 */
export type WizardFlow = 'hybrid' | 'management' | 'field-for-hybrid';

/** What each flow reports as `recommendation_type`, so one funnel can be split by flow. */
const TRACKED_TYPES: Record<WizardFlow, SeedRecommendationType> = {
    hybrid: 'hybrid',
    management: 'management',
    'field-for-hybrid': 'field_for_hybrid',
};

function SeedRecommendationWizard({
    crop = 'corn',
    variant = 'hybrid',
}: {
    crop?: 'corn' | 'soy';
    variant?: WizardFlow;
}) {
    const isManagement = variant === 'management';
    const isFieldForHybrid = variant === 'field-for-hybrid';
    // Both flows that start from a known hybrid produce a management recommendation: the
    // product is already settled, so what is left to recommend is how to manage it. Only the
    // flow that ranks hybrids for a field produces a hybrid recommendation.
    const savesAsManagement = isManagement || isFieldForHybrid;
    const saveVariant: RecommendationVariant = savesAsManagement ? 'management' : 'hybrid';
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const entrySource = query.get('entrySource') as SeedRecommendationEntrySource;
    // Entry points that already know the field (field list, crop monitoring, the RV modal with a
    // field picked, …) pass it in and land straight on the environment step.
    const preselectedFieldId = Number(query.get('fieldId')) || undefined;
    // The hybrid this flow is finding a field for. Fixed for the whole wizard.
    const fixedProductId = Number(query.get('product_profile')) || undefined;
    // Product exploration hands the management flow the hybrid the user was already looking at,
    // so "Elegir producto" would be a one-option step asking a question already answered.
    const skipsProductStep = isManagement && !!fixedProductId;
    // Tracks the `steps` array assembled at the bottom of this component — the drafting effect
    // below keys on reaching the last one, and every flow ends on the management step.
    const stepCount = !isFieldForHybrid && skipsProductStep ? 3 : 4;
    const dispatch = useDispatch();
    const [activeStep, setActiveStep] = useState<number>(preselectedFieldId && !isFieldForHybrid ? 1 : 0);
    // Reported up by the environment step, whose queries the wizard itself must not mount.
    const [environmentLoading, setEnvironmentLoading] = useState(false);
    const [selectedField, setSelectedField] = useState<Field | undefined>(undefined);

    // One id per attempt, shared by every event it fires. It lives here rather than in a step:
    // applying a simulated planting date sends the user back to the environment step, and the
    // attempt has to keep its id across that.
    const [flowId] = useState(() => uuidv4());
    const simulations = useRef(0);
    const trackedType = TRACKED_TYPES[variant];
    const flow = useMemo(
        () => ({ flowId, crop, recommendationType: trackedType, simulations }),
        [flowId, crop, trackedType]
    );
    // Read when the user leaves, from cleanup that would otherwise see the first render's values.
    const lastStep = useRef<SeedRecommendationStep | undefined>(undefined);
    const lastTab = useRef<SeedRecommendationManagementTab | undefined>(undefined);
    const finished = useRef(false);

    // The field list is fetched with a reduced field set (no country, no zones), so the
    // crop config and the environment step need the full record.
    const fieldId = selectedField?.id ?? preselectedFieldId;
    const { data: field } = useFieldById({ id: fieldId as number }, { enabled: !!fieldId });

    // Adopt a preselected field as the selection, so going back to step 1 shows it picked.
    useEffect(() => {
        if (!selectedField && field?.id === preselectedFieldId) setSelectedField(field);
    }, [field, preselectedFieldId, selectedField]);

    const appInstance = useSelector(getAppInstance);
    const defaultCropConfig = useDefaultCropConfig({ crop, field });
    const { risks } = useFieldRisks(field);
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const units = useUnitProvider();
    const navigate = useNavigate();
    const sessionBrands = useSelector(getSessionBrands);
    const country = useSelector(getSessionCountry);

    // Everything the save payload needs. These are queries and memos only — no defaulting
    // effects — so mounting them at the view level is safe (unlike useRecommendationEditData,
    // which rewrites the environment on mount).
    const { featuredProducts, otherProducts } = useHybrids({ field, crop });
    const mappedProducts = useMemo(
        () =>
            [...featuredProducts, ...otherProducts].reduce<Record<number, unknown>>((byId, product) => {
                byId[product.id] = product;
                return byId;
            }, {}),
        [featuredProducts, otherProducts]
    );
    // `getCropConfig` returns `{}` before a recommendation exists, so normalise the fields
    // `useRecommendationResultData` reads rather than just checking for a falsy config.
    const cropConfigForSave = useMemo(
        () => ({
            ...cropConfig,
            crop: cropConfig?.crop || crop,
            products: cropConfig?.products || [],
        }),
        [cropConfig, crop]
    );
    const { productsWithAPIData, pestControlPlans, catalogBrands, isResultDataLoading } = useRecommendationResultData({
        field,
        crop,
        cropConfig: cropConfigForSave,
        mappedProducts,
    });
    const { save, isSaving, savedRecommendation } = useSaveRecommendation();

    // The selected hybrid's derived figures, for the snapshot the dashboard list reads back.
    // The management step mounts the same hook, and SWR dedupes the queries behind it.
    const { hybrids: managementHybrids } = useManagementData({ field, crop });

    // The brand of the hybrid actually chosen, the way the read-only page resolves it. An
    // instance can publish more than one corn brand, so taking the first session brand would
    // stamp every recommendation — Preceon ones included — with whichever came back first.
    const recommendationBrand = useMemo(
        () =>
            productsWithAPIData?.[0]?.profile?.product?.brand ??
            (cropConfig ? sessionBrands[cropConfig.crop]?.[0] : undefined),
        [productsWithAPIData, sessionBrands, cropConfig]
    );

    const saveArgs = {
        recommendation,
        productsWithAPIData,
        brand: recommendationBrand,
        crop,
        pestControlPlans,
        field,
        catalogBrands,
        cropConfig: cropConfigForSave,
        variant: saveVariant,
        managementHybrid: savesAsManagement ? managementHybrids[0] : undefined,
        flowId,
        recommendationType: trackedType,
    };

    // Same shape `useSavedRecommendation` builds for the result page's `parsedRecommendation`,
    // so the management step's copy-as-image button can hit the same endpoint before "Terminar".
    // The render reads the posted payload, so it is built from the wizard as it stands now — the
    // draft was saved on arriving at this step, before its queries resolved or a hybrid was
    // added, and only lends its id. Withheld until those queries settle, so a copy can't capture
    // an empty chart.
    const recommendationForCopy =
        savedRecommendation && recommendation && !isResultDataLoading
            ? ({
                  ...savedRecommendation,
                  ...buildRecommendationBody({ ...saveArgs, status: 'draft' }).backup,
                  id: savedRecommendation.id,
              } as unknown as Recommendation)
            : undefined;

    // Keep a draft from the moment there is something worth keeping, so abandoning the wizard
    // doesn't throw the work away. `Terminar` promotes the same record.
    const hasSelection = !!cropConfig?.products?.length;
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on reaching the last step
    useEffect(() => {
        if (activeStep < stepCount - 1 || !hasSelection || !recommendation) return;
        save({ ...saveArgs, savedRecommendation, status: 'draft', notify: false });
    }, [activeStep, hasSelection]);

    const handleFinish = async () => {
        mpTrackEvent('Seed recommendation finished', flowEventProps(flow));
        // `saved` is the status the density wizard always wrote, kept so the record reads the
        // same to everything that lists and filters these.
        const saved = await save({
            ...saveArgs,
            savedRecommendation,
            status: savesAsManagement ? 'saved' : 'read_only',
        });
        if (!saved?.id) return;
        // Only now: a failed save leaves the user here, and leaving after that is still an exit.
        finished.current = true;
        navigate(
            savesAsManagement
                ? reverse('recommendations.CornDensityRecommendation', {
                      params: { recommendationId: saved.id },
                  })
                : reverse('recommendations.RecommendationResultV2', {
                      params: { recommendationId: saved.id, crop },
                  })
        );
    };

    const environmentSummary = useMemo(() => {
        if (!cropConfig?.environment) return undefined;
        const value = units.toYield(cropConfig.environment, crop, 0);
        return (
            <Stack p={1} borderRadius='4px' sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                <Typography variant='subtitle1' fontWeight={700} color='primary'>
                    {`${value.localeValue} ${value.unit}`}
                </Typography>
                {cropConfig.plantingDate && (
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {_('Fecha de siembra: {{date}}', {
                            date: formatDate(new Date(cropConfig.plantingDate)),
                        })}
                    </Typography>
                )}
            </Stack>
        );
    }, [cropConfig?.environment, cropConfig?.plantingDate, crop, units]);

    const conditionsSummary = useMemo(() => {
        if (!cropConfig?.plantingDate) return undefined;
        const climate = getClimatePatternChoices().find(
            (choice: { value: number }) => choice.value === cropConfig.climatePattern
        )?.label;
        // 0% is a real reading, so the moisture line keys on "has a value", not truthiness.
        const hasMoisture = cropConfig.soilMoisture !== undefined && cropConfig.soilMoisture !== null;
        const details = [
            hasMoisture ? _('Humedad {{value}}%', { value: cropConfig.soilMoisture }) : undefined,
            climate && _('Año {{pattern}}', { pattern: climate }),
        ].filter(Boolean);
        return (
            <Stack p={1} borderRadius='4px' sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                <Typography variant='subtitle1' fontWeight={700} color='primary'>
                    {_('Fecha de siembra: {{date}}', { date: formatDate(new Date(cropConfig.plantingDate)) })}
                </Typography>
                {details.length > 0 && (
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {details.join(' - ')}
                    </Typography>
                )}
            </Stack>
        );
    }, [cropConfig?.plantingDate, cropConfig?.soilMoisture, cropConfig?.climatePattern]);

    const hybridsSummary = useMemo(() => {
        const selectedIds: number[] = cropConfig?.products || [];
        if (!selectedIds.length) return undefined;
        // Selection order, not catalogue order, so the recap matches the cards.
        const names = selectedIds
            .map((id) => (mappedProducts[id] as { catalogName?: string } | undefined)?.catalogName)
            .filter(Boolean);
        if (!names.length) return undefined;
        return (
            <Stack p={1} borderRadius='4px' sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                <Typography variant='subtitle1' fontWeight={700} color='primary'>
                    {names.join(' - ')}
                </Typography>
            </Stack>
        );
    }, [cropConfig?.products, mappedProducts]);

    // Crop config handed over by another flow (the density recommendation), overriding the
    // zone defaults. Seeded here rather than left to the environment step: React runs child
    // effects before parent ones, so a step applying them first would just be overwritten by
    // this seed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: derived from the URL, fixed per mount
    const handoffCropConfig = useMemo(() => {
        const plantingDate = query.get('planting_date');
        const climatePattern = Number(query.get('climate_pattern'));
        const soilMoisture = Number(query.get('soil_moisture'));
        return {
            ...(plantingDate ? { plantingDate } : {}),
            ...(Number.isFinite(climatePattern) && query.get('climate_pattern') ? { climatePattern } : {}),
            ...(Number.isFinite(soilMoisture) && query.get('soil_moisture') ? { soilMoisture } : {}),
        };
    }, [search]);

    // The product-first flow asks its questions before any field exists, so the slice has to be
    // seeded from something else: the hybrid's own zones, which carry the same planting windows
    // the field's would.
    const { data: fixedProduct } = useCatalogProductById(
        { id: fixedProductId, crop },
        { enabled: isFieldForHybrid && !!fixedProductId }
    );
    const productCropConfig = useSimplifiedDefaultCropConfig({
        crop,
        zones: fixedProduct?.zones || [],
        countryCode: country?.code,
    });
    const seededProduct = useRef(false);
    useEffect(() => {
        if (!isFieldForHybrid || seededProduct.current || !(productCropConfig as { crop?: string })?.crop) return;
        seededProduct.current = true;
        dispatch(
            updateActiveRecommendation({
                appInstance: appInstance.id,
                crops: [{ ...productCropConfig, products: fixedProductId ? [fixedProductId] : [] }],
            })
        );
    }, [appInstance.id, dispatch, isFieldForHybrid, productCropConfig, fixedProductId]);

    // Seed the same `activeRecommendation` store slice the recommendation-edit page
    // uses, so the environment step can reuse its derivation cascade. Guarded per field:
    // `defaultCropConfig`'s identity changes while its own queries are still resolving, and
    // re-seeding would throw away whatever the user has already configured on step 2.
    const seededFieldId = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (!field || !defaultCropConfig || seededFieldId.current === field.id) return;
        seededFieldId.current = field.id;
        dispatch(
            updateActiveRecommendation({
                appInstance: appInstance.id,
                field: String(field.id),
                crops: [
                    isFieldForHybrid
                        ? // The conditions were answered on step 1, before this field existed;
                          // the field's defaults fill in the rest without overwriting them.
                          { ...defaultCropConfig, ...cropConfig, ...handoffCropConfig }
                        : {
                              ...defaultCropConfig,
                              ...handoffCropConfig,
                              // With the product step skipped nothing else would ever select the
                              // hybrid, and this dispatch replaces the whole config — so the
                              // handed-over one has to ride along with it.
                              ...(skipsProductStep ? { products: [fixedProductId] } : {}),
                          },
                ],
            })
        );
        // biome-ignore lint/correctness/useExhaustiveDependencies: runs once per field, and the
        // config read here is whatever the earlier steps left behind
    }, [appInstance.id, dispatch, field, defaultCropConfig, handoffCropConfig, isFieldForHybrid]);

    useEffect(() => {
        return () => {
            dispatch(updateActiveRecommendation(undefined));
        };
    }, [dispatch]);

    // The recommendation-edit page fired this on mount; the wizard no longer navigates there, so
    // the event moves to the step that replaces it: the environment, in every flow. It also
    // replaces the density `began` the management entry points used to fire before navigating.
    // biome-ignore lint/correctness/useExhaustiveDependencies: `lastStep` is set from `activeStep` during render
    useEffect(() => {
        if (lastStep.current === 'environment')
            mpTrackEvent('Seed recommendation began', {
                crop,
                entry_source: entrySource,
                flow_id: flowId,
                recommendation_type: trackedType,
                product_id: fixedProductId,
            });
    }, [activeStep, crop, entrySource, flowId, trackedType, fixedProductId]);

    // Leaving through the app unmounts the wizard; closing or reloading the tab doesn't, so
    // `pagehide` covers that, over a beacon that survives the page going away.
    const pendingEnd = useRef<number | undefined>(undefined);
    useEffect(() => {
        // StrictMode remounts every effect once in development. Deferring the unmount report lets
        // that remount cancel it, instead of logging an exit the moment the wizard opens.
        window.clearTimeout(pendingEnd.current);
        let ended = false;
        const end = () => {
            if (ended || finished.current || !lastStep.current) return;
            ended = true;
            mpTrackEvent(
                'Seed recommendation flow:Ended',
                {
                    ...flowEventProps(flow),
                    last_step: lastStep.current,
                    ...(lastStep.current === 'management' ? { last_tab: lastTab.current } : {}),
                },
                { transport: 'sendBeacon', send_immediately: true }
            );
        };
        window.addEventListener('pagehide', end);
        return () => {
            window.removeEventListener('pagehide', end);
            pendingEnd.current = window.setTimeout(end);
        };
    }, [flow]);

    // The label above each step's title, and the name the flow goes by throughout the wizard.
    const recommendationType = savesAsManagement ? _('Recomendación de manejo') : _('Recomendación de híbrido');

    const fieldStep: Step = {
        sidebarName: _('Selección de lote'),
        recommendationType,
        title: _('Elegí el lote para generar la recomendación'),
        subtitle: _('Seleccioná un lote para obtener una recomendación ajustada a sus condiciones específicas.'),
        content: (
            <FieldSelect
                field={selectedField}
                setField={(field) => {
                    setSelectedField(field);
                }}
                // Ranks the list by how this hybrid yields under the conditions from step 1,
                // which is the whole point of asking them first.
                selectedProduct={isFieldForHybrid ? fixedProductId : undefined}
                plantingDate={isFieldForHybrid ? cropConfig?.plantingDate : undefined}
                soilMoisture={isFieldForHybrid ? cropConfig?.soilMoisture : undefined}
            />
        ),
        sidebarSummary: field ? <FieldSummary field={field} risks={risks} /> : undefined,
        onClickNextDisabled: () => selectedField === undefined,
        // Step 1 in every flow but the product-first one, where the conditions come first.
        onClickBackDisabled: () => !isFieldForHybrid,
    };

    const environmentStep: Step = {
        sidebarName: _('Configurar ambiente'),
        recommendationType,
        title: _('Configurá el ambiente del lote'),
        subtitle: _(
            'Con modelos de simulación, ensayos y rindes históricos de la zona te mostramos la situación más probable para este lote. Ajustá las variables y el ambiente con lo que sabés del lote.'
        ),
        content: field ? (
            <EnvironmentStep
                field={field}
                crop={crop}
                showConditions={!isFieldForHybrid}
                onLoadingChange={setEnvironmentLoading}
            />
        ) : (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        ),
        sidebarSummary: environmentSummary,
        // The environment, moisture and soil-type options all arrive from the cascade this step
        // mounts, and the next step's ranking is keyed on them — advancing mid-flight would rank
        // hybrids against a half-defaulted environment. `!field` covers the window before the
        // step is mounted at all, when nothing has reported a loading state yet.
        onClickNextDisabled: () => !field || environmentLoading,
    };

    const hybridsStep: Step = {
        sidebarName: isManagement ? _('Elegir producto') : _('Híbridos recomendados'),
        recommendationType,
        title: isManagement
            ? _('Elegí el híbrido para ver el manejo recomendado')
            : _('Elegí los híbridos para este lote'),
        subtitle: isManagement
            ? _('Seleccioná el híbrido que compró el productor para ver las recomendaciones específicas.')
            : _(
                  'Híbridos recomendados según el ambiente y las condiciones del lote. Seleccioná {{count}} para ver las recomendaciones específicas.',
                  { count: settings.maxProductSelection }
              ),
        content: field ? (
            isManagement ? (
                <ProductStep field={field} crop={crop} />
            ) : (
                <HybridsStep field={field} crop={crop} />
            )
        ) : (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        ),
        sidebarSummary: hybridsSummary,
        onClickNextDisabled: () => !cropConfig?.products?.length,
    };

    // Applying a simulated planting date on the phenology tab clears the selection — the hybrids
    // were chosen for the old conditions — and sends the user back to reconfigure the
    // environment. Flows with no product step fixed their hybrid up front and have nowhere to
    // pick it again, so they keep it.
    const handlePlantingDateApplied = () => {
        if (isFieldForHybrid || skipsProductStep) {
            dispatch(
                updateActiveRecommendationCropConfig({ key: 'products', value: fixedProductId ? [fixedProductId] : [] })
            );
        }
        // Looked up rather than hard-coded: the environment step sits at a different index in
        // the product-first flow, where the conditions come first.
        goToStep(steps.indexOf(environmentStep), 'planting_date_applied');
    };

    const handleTabViewed = (tab: SeedRecommendationManagementTab) => {
        lastTab.current = tab;
        mpTrackEvent('Seed recommendation management tab viewed', { ...flowEventProps(flow), tab });
    };

    const managementStep: Step = {
        sidebarName: _('Manejo recomendado'),
        recommendationType,
        title: _('Conocé el manejo recomendado'),
        subtitle: savesAsManagement
            ? _('Densidad, fenología y fertilización para este lote, según el ambiente y el híbrido seleccionado.')
            : _('Densidad, fenología y fertilización para este lote, según el ambiente y los híbridos seleccionados.'),
        content: field ? (
            <ManagementStep
                field={field}
                crop={crop}
                onEditEnvironment={handlePlantingDateApplied}
                maxHybrids={savesAsManagement ? 1 : undefined}
                recommendationForCopy={recommendationForCopy}
                onTabViewed={handleTabViewed}
            />
        ) : (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        ),
        onClickNextText: _('Terminar'),
        onClickNext: handleFinish,
        // What "Terminar" saves is what the PDF renders from, with no fetching of its own — so it
        // waits for the curves, phenology and fertilization rather than storing their gaps.
        onClickNextDisabled: () => !hasSelection || isResultDataLoading,
    };

    const conditionsStep: Step = {
        sidebarName: _('Condiciones ambientales'),
        recommendationType,
        title: _('Ingresá la fecha de siembra y la humedad del suelo'),
        subtitle: _('Con estos datos estimamos cómo rinde {{hybrid}} en los lotes de tus productores.', {
            hybrid: fixedProduct?.catalogName || '',
        }),
        content: <ConditionsStep productId={fixedProductId} crop={crop} />,
        sidebarSummary: conditionsSummary,
        onClickBackDisabled: () => true,
        onClickNextDisabled: () => !cropConfig?.plantingDate || !cropConfig?.climatePattern,
    };

    // The hybrid is already known here, so instead of choosing one the wizard looks for the
    // field it suits — which means the conditions the ranking depends on come first.
    const stepKeys: SeedRecommendationStep[] = isFieldForHybrid
        ? ['conditions', 'field', 'environment', 'management']
        : skipsProductStep
          ? ['field', 'environment', 'management']
          : ['field', 'environment', isManagement ? 'product' : 'hybrids', 'management'];
    const stepsByKey: Record<SeedRecommendationStep, Step> = {
        conditions: conditionsStep,
        field: fieldStep,
        environment: environmentStep,
        hybrids: hybridsStep,
        // The same step in the management flow, where it picks the one hybrid already bought.
        product: hybridsStep,
        management: managementStep,
    };
    const steps = stepKeys.map((key) => stepsByKey[key]);
    lastStep.current = stepKeys[activeStep];

    // Every move between steps goes through here — "Siguiente", "Atrás" and the sidebar, which
    // only ever goes back — so the funnel sees each one.
    const goToStep = (index: number, reason: 'manual' | 'planting_date_applied' = 'manual') => {
        trackStepChange(flow, stepKeys, activeStep, index, reason);
        setActiveStep(index);
    };

    return (
        <SeedRecommendationFlowContext.Provider value={flow}>
            {/* `save` queues rather than drops a second call, so without `areActionsLoading` a double
                tap on "Terminar" would save twice and show the success snackbar twice. */}
            {RecommendationCreate({ steps, activeStep, setActiveStep: goToStep, areActionsLoading: isSaving })}
        </SeedRecommendationFlowContext.Provider>
    );
}

/**
 * Field selection for soy, which has no wizard: the v2 steps are built on corn-only data
 * (density curve, phenology, defensive profile). Picking the field hands off to the
 * single-page editor, the same one every other soy entry point opens.
 */
function SoyFieldSelect() {
    const { search } = useLocation();
    const entrySource = new URLSearchParams(search).get('entrySource') ?? undefined;
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState<number>(0);
    const [field, setField] = useState<Field | undefined>(undefined);

    const steps: Step[] = [
        {
            sidebarName: _('Selección de lote'),
            recommendationType: _('Recomendación de semilla'),
            title: _('Elegí el lote para generar la recomendación'),
            subtitle: _('Seleccioná un lote para obtener una recomendación ajustada a sus condiciones específicas.'),
            content: <FieldSelect field={field} setField={setField} />,
            onClickNext: () => {
                if (!field) return;
                navigate(seedRecommendationUrl({ crop: 'soy', fieldId: field.id, entrySource }));
            },
            onClickNextDisabled: () => field === undefined,
            onClickBackDisabled: () => true,
        },
        {
            sidebarName: _('Configurar recomendación'),
            content: '',
        },
    ];
    return RecommendationCreate({ steps, activeStep, setActiveStep });
}

// Separate components rather than an early return, so switching `:crop` in place swaps the
// whole tree instead of changing the hook count of a mounted one.
function RecommendationFieldSelect(props: { crop?: 'corn' | 'soy'; variant?: WizardFlow }) {
    if (props.crop === 'soy') return <SoyFieldSelect />;
    return <SeedRecommendationWizard {...props} />;
}

export default RecommendationFieldSelect;
